import { useState } from "react";
import { Button } from "@/design-system/primitives";
import {
  useWithdrawDoseChangeHold,
  type DoseChangeHold,
  type FoldedDoseRequest,
  type ReleaseReason,
} from "@/lib/api/doseChangeHolds";

const REASONS: Record<
  ReleaseReason,
  {
    label: string;
    help: string;
    confirm: string;
    placeholder: string;
  }
> = {
  no_change_available: {
    label: "No dose change available",
    help: "They asked for a dose or schedule we don't offer.",
    confirm:
      "The patient asked for a dose or schedule we don't offer, did not report side effects or ask to lower or stop, and wants their current prescription to keep shipping.",
    placeholder: "e.g. 200 mg weekly is the highest dose we offer.",
  },
  not_a_dose_request: {
    label: "Not a dose request (misread message)",
    help: 'Nothing the patient wrote asks to change the dose. A follow-up such as "Respond please." usually follows a real ask: read their earlier messages first.',
    confirm:
      "I read the patient's earlier messages. None asks to change the dose or reports side effects, and they want their current prescription to keep shipping.",
    placeholder:
      "e.g. Asked how to store the vial; no dose ask anywhere in the chat.",
  },
};

/** Close a request without a dose change: either the patient asked for a dose
 * or schedule we don't offer, or the message was never a dose request. Nothing
 * changes: the hold is released and fills continue on the prescription
 * already in effect. Side effects, lowering or stopping, doses that are
 * offered, and requests with other asks folded in stay with the prescriber. */
export function NoDoseChangeAvailable({
  hold,
  folded = [],
}: {
  hold: DoseChangeHold;
  /** Asks folded into this request. The server refuses to close one that
   * has any: a later ask may report side effects or ask to lower or stop. */
  folded?: Pick<FoldedDoseRequest, "id">[];
}) {
  const withdraw = useWithdrawDoseChangeHold(hold.patient_id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReleaseReason>("no_change_available");
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const ready = confirmed && note.trim().length >= 5 && !withdraw.isPending;
  const copy = REASONS[reason];

  if (folded.length > 0)
    return (
      <p className="text-12 text-ink-muted">
        Other asks are folded into this request, so the prescriber reviews it.
      </p>
    );
  if (!open)
    return (
      <Button
        type="button"
        variant="secondary"
        className="self-start"
        onClick={() => setOpen(true)}
      >
        Close without dose change
      </Button>
    );
  return (
    <form
      className="flex flex-col gap-10 border border-line p-10 bg-raised"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        withdraw.mutate(
          {
            requestId: hold.id,
            body: {
              reason,
              note: note.trim(),
              confirmed: true,
              expected_resolution_version: hold.resolution_version,
            },
          },
          { onSuccess: () => setOpen(false) },
        );
      }}
    >
      <p className="text-14">
        Closes this request without a prescription change. The hold is released
        and fills continue on the current prescription. Nothing is sent to the
        patient; answer them in the conversation.
      </p>
      <fieldset className="flex flex-col gap-6">
        <legend className="text-14 font-medium">Why</legend>
        {(Object.keys(REASONS) as ReleaseReason[]).map((key) => (
          <label key={key} className="text-14 flex gap-8">
            <input
              type="radio"
              name={`release-reason-${hold.id}`}
              value={key}
              checked={reason === key}
              onChange={() => {
                setReason(key);
                setConfirmed(false);
              }}
            />
            <span>
              {REASONS[key].label}
              <span className="block text-12 text-ink-muted">
                {REASONS[key].help}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
      <p className="text-14">
        Leave it for the prescriber if the patient reports side effects, asks to
        lower the dose, or asks for a dose we do offer. If they want to pause or
        stop, pause or cancel their subscription instead: closing this lets the
        next fill ship.
      </p>
      <label className="text-14 flex flex-col gap-5">
        Note for the chart
        <textarea
          required
          minLength={5}
          maxLength={2000}
          value={note}
          placeholder={copy.placeholder}
          onChange={(event) => setNote(event.target.value)}
          className="bg-page border border-line p-10"
        />
      </label>
      <label className="text-14 flex gap-8">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        {copy.confirm}
      </label>
      <div className="flex gap-10">
        <Button type="submit" disabled={!ready}>
          Close request
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={withdraw.isPending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
      {withdraw.isError && (
        <p role="alert" className="text-14 text-danger">
          {withdraw.error instanceof Error
            ? withdraw.error.message
            : "The request could not be closed. Refresh and retry."}
        </p>
      )}
    </form>
  );
}
